// /src/modules/profileSummary.ts
import type { Me } from "../core/me";
import { applyTheme, type Theme } from "../core/theme";
import { makeIdentityKey } from "./identityKey";
import { getProfile } from "./profileApi";

export const PROFILE_SUMMARY_UPDATED_EVENT = "context:profile-summary-updated";

export type ProfileSummary = {
  identityKey: string;
  displayName?: string;
  theme?: Theme;
  updatedAtUtc?: string;
};

const CACHE_KEY_PREFIX = "profile_summary_v1:";

function cacheKeyForIdentity(identityKey: string): string {
  return `${CACHE_KEY_PREFIX}${identityKey}`;
}

function readCache(identityKey: string): ProfileSummary | null {
  try {
    const raw = sessionStorage.getItem(cacheKeyForIdentity(identityKey));
    if (!raw) return null;
    const obj = JSON.parse(raw) as Partial<ProfileSummary>;
    if (!obj.identityKey) return null;
    // Defensive: ensure stored identity matches requested identity
    if (obj.identityKey !== identityKey) return null;

    return {
      identityKey: obj.identityKey,
      displayName: obj.displayName,
      theme: obj.theme as Theme | undefined,
      updatedAtUtc: obj.updatedAtUtc,
    };
  } catch {
    return null;
  }
}

function writeCache(v: ProfileSummary) {
  try {
    sessionStorage.setItem(cacheKeyForIdentity(v.identityKey), JSON.stringify(v));
  } catch {
    // ignore
  }
}

// keep this function, but make it clear all identities
export function clearProfileSummaryCache() {
  try {
    // minimal approach: clear only the known prefix keys
    // sessionStorage has no prefix delete, so iterate keys
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(CACHE_KEY_PREFIX)) sessionStorage.removeItem(k);
    }
  } catch {
    // ignore
  }
}

export async function getProfileSummaryForIdentity(me: Me): Promise<ProfileSummary | null> {
  if (!me?.authenticated) return null;

  const identityKey = makeIdentityKey(me);

  const cached = readCache(identityKey);
  if (cached) {
    applyTheme(cached.theme);
    return cached;
  }

  const p = await getProfile();

  const summary: ProfileSummary = {
    identityKey,
    displayName: p.displayName ?? undefined,
    theme: (p.theme ?? "day") as Theme,
    updatedAtUtc: p.updatedAtUtc,
  };

  writeCache(summary);
  applyTheme(summary.theme);
  return summary;
}

export function tryApplyCachedThemeForIdentity(me: Me): boolean {
  if (!me?.authenticated) return false;

  const identityKey = makeIdentityKey(me);
  const cached = readCache(identityKey); // uses identity-scoped key now
  if (!cached?.theme) return false;

  applyTheme(cached.theme);
  return true;
}

export function setProfileSummaryForIdentity(me: Me, summary: ProfileSummary): void {
  if (!me?.authenticated) return;

  const identityKey = makeIdentityKey(me);
  if (summary.identityKey !== identityKey) {
    // ensure we never write under the wrong identity
    summary = { ...summary, identityKey };
  }

  writeCache(summary);
}

